import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  useHistory,
  useLocation,
  useRouteMatch,
  Route,
} from 'react-router-dom';
import { fetchUserInfo } from 'actions/user';
import {
  selectIsPremiumAccount,
  selectIsUserFetching,
  selectIsUserLoggedIn,
} from '../state/user/selectors';
import Layout from '../Layouts/Layout';
import PremiumLayout from '../Layouts/PremiumLayout';

if (process.env.NODE_ENV !== 'test') {
  require('styles/application.scss');
}

const getParams = ({ query = '' }) => {
  const pl = /\+/g; // Regex for replacing addition symbol with a space
  const search = /([^&=]+)=?([^&]*)/g;
  const decode = s => decodeURIComponent(s.replace(pl, ' '));

  let match;
  let urlParams = {};
  while ((match = search.exec(query))) {
    urlParams[decode(match[1])] = decode(match[2]);
  }

  return urlParams;
};

const RouteWrap = ({ component: Page, ...props }) => {
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const params = useMemo(() => {
    const query = location.search.substring(1);
    return getParams({ query });
  }, [location.search]);

  return (
    <Page
      {...props}
      history={history}
      params={{ ...match.params, ...params }}
      match={match}
      location={{ ...location, query: params }}
    />
  );
};

const PrivateRoute = ({ noWrapper, component, ...props }) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isFetching = useSelector(selectIsUserFetching);
  const isLoggedIn = useSelector(selectIsUserLoggedIn);
  const isPremiumAccount = useSelector(selectIsPremiumAccount);

  useEffect(() => {
    dispatch(fetchUserInfo());
  }, [dispatch]);

  const AppLayout = useMemo(() => (isPremiumAccount ? PremiumLayout : Layout), [
    isPremiumAccount,
  ]);

  // Redirects
  if (isFetching) return null;
  if (!isLoggedIn) {
    return <Redirect to="/logged-out" />;
  }

  if (isPremiumAccount && !location.pathname.includes('/premium')) {
    return <Redirect to="/premium/overview" />;
  }

  if (!isPremiumAccount && location.pathname.includes('/premium')) {
    return <Redirect to="/overview" />;
  }

  if (noWrapper) {
    return (
      <Route {...props}>
        <RouteWrap {...props} component={component} />
      </Route>
    );
  }

  return (
    <Route {...props}>
      <div className="application">
        <AppLayout>
          <RouteWrap {...props} component={component} />
        </AppLayout>
      </div>
    </Route>
  );
};
export default PrivateRoute;
